import CheckIcon from '@mui/icons-material/Check'
import SyncIcon from '@mui/icons-material/Sync'
import { IconButton, Stack, Typography } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { TimeClockWithRelated } from '../../hooks/useGetTimeClockWithRelated/useGetTimeClockWithRelated'
import { useUpdateTimeClockMutation } from '../../redux/api/timeClockApi'
import {
  ProductivityStatusEntry,
  RepairOrderWithRelated,
  TimeClockEntry
} from '../../sdk'
import { describeRepairOrderWithVehicle } from '../../utils/describeRepairOrderWithRelated/describeRepairOrderWithRelated'
import { ControlledMuiSelect } from '../FormField'
import { CustomDateTimePicker } from '../SharedUI/CustomDateTimePicker'

interface NewTimeClockInput {
  status: string
  repairOrder: string
}

export const UpdateTimeClockForm = ({
  timeClockWithRelated,
  lastTimeClock,
  nextTimeClock,
  productivityStatuses,
  recentRepairOrders,
  submitCallback
}: {
  timeClockWithRelated: TimeClockWithRelated
  lastTimeClock: TimeClockEntry | undefined
  nextTimeClock: TimeClockEntry | undefined
  productivityStatuses: ProductivityStatusEntry[]
  recentRepairOrders: RepairOrderWithRelated[]
  submitCallback: () => void
}) => {
  const activeStatuses = useMemo(
    () => productivityStatuses.filter((p) => !p.info!.archivedAt),
    [productivityStatuses]
  )
  const outDatedStatus = useMemo(() => {
    if (
      !timeClockWithRelated.relatedStatus ||
      activeStatuses.find(
        (s) => s.id === timeClockWithRelated.relatedStatus!.id
      )
    ) {
      return []
    }
    return [timeClockWithRelated.relatedStatus]
  }, [activeStatuses, timeClockWithRelated.relatedStatus])
  const statusesToDisplay = useMemo(
    () => [...outDatedStatus, ...activeStatuses],
    [activeStatuses, outDatedStatus]
  )
  const [clockIn, setClockIn] = useState<Dayjs | null>(
    dayjs(timeClockWithRelated.info!.clockInDateTime)
  )
  const [clockOut, setClockOut] = useState<Dayjs | null>(
    timeClockWithRelated.info!.clockOutDateTime
      ? dayjs(timeClockWithRelated.info!.clockOutDateTime)
      : null
  )

  const isStartMatchingLastEntry = useMemo(
    () =>
      !!lastTimeClock?.info!.clockOutDateTime &&
      clockIn?.toDate().getTime() ===
        lastTimeClock.info!.clockOutDateTime.getTime(),
    [clockIn, lastTimeClock?.info]
  )
  const isEndMatchingNextEntry = useMemo(
    () =>
      !!nextTimeClock?.info!.clockInDateTime &&
      clockOut?.toDate().getTime() ===
        nextTimeClock.info!.clockInDateTime.getTime(),
    [clockOut, nextTimeClock?.info]
  )
  const [updateTimeClock] = useUpdateTimeClockMutation()
  const { accessToken, userId } = useAccessTokenAndShopId()
  const { handleSubmit, control, watch, setValue } = useForm<NewTimeClockInput>(
    {
      mode: 'onBlur',
      defaultValues: { ...timeClockWithRelated.info! }
    }
  )
  const statusInput = watch('status')
  useEffect(() => {
    if (
      !activeStatuses.find((s) => s.id === statusInput)?.info!
        .requireRepairOrder
    ) {
      setValue('repairOrder', '')
    } else {
      setValue('repairOrder', timeClockWithRelated.info!.repairOrder || '')
    }
  }, [
    activeStatuses,
    timeClockWithRelated.info,
    recentRepairOrders,
    setValue,
    statusInput
  ])

  const onSubmit: SubmitHandler<NewTimeClockInput> = useCallback(
    (data) => {
      const now = new Date()
      updateTimeClock({
        authorization: accessToken,
        timeClockEntry: {
          id: timeClockWithRelated.id!,
          info: {
            ...timeClockWithRelated.info,
            modifiedAt: now,
            modifiedBy: userId,
            clockInDateTime: clockIn ? clockIn.toDate() : undefined,
            clockOutDateTime: clockOut ? clockOut.toDate() : undefined,
            repairOrder: activeStatuses.find((s) => s.id === data.status)?.info!
              .requireRepairOrder
              ? data.repairOrder
              : undefined,
            status: data.status
          }
        }
      })
      submitCallback()
    },
    [
      updateTimeClock,
      accessToken,
      timeClockWithRelated,
      userId,
      clockIn,
      clockOut,
      activeStatuses,
      submitCallback
    ]
  )
  return (
    <form onSubmit={handleSubmit(onSubmit)} id="updateTimeClock">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <ControlledMuiSelect
          name="status"
          control={control}
          options={statusesToDisplay.map((s) => ({
            id: s.id!,
            description: s.info!.title!
          }))}
          label="Status"
          variant="standard"
          disabled={statusesToDisplay.length === 0}
        />
        <ControlledMuiSelect
          name="repairOrder"
          control={control}
          options={recentRepairOrders.map((ro) => ({
            id: ro.repairOrder!.id!,
            description: describeRepairOrderWithVehicle(ro)
          }))}
          label="Repair Order"
          disabled={
            statusesToDisplay.length === 0 ||
            !statusesToDisplay.find((s) => s.id === statusInput)?.info!
              .requireRepairOrder
          }
          variant="standard"
        />
      </Stack>
      <Stack direction="row" spacing={1} mt={2}>
        <Stack>
          <CustomDateTimePicker
            label="Start"
            dateTime={clockIn}
            setDateTime={setClockIn}
          />
          {lastTimeClock && !isStartMatchingLastEntry && (
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                onClick={() =>
                  setClockIn(dayjs(lastTimeClock.info!.clockOutDateTime))
                }>
                <SyncIcon />
              </IconButton>
              <Typography variant="body2" color="grey">
                Reset to match the last end time
              </Typography>
            </Stack>
          )}
          {isStartMatchingLastEntry && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckIcon color="success" />
              <Typography variant="body2" color="grey">
                Matched the last end time
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack>
          <CustomDateTimePicker
            label="End"
            dateTime={clockOut}
            setDateTime={setClockOut}
          />
          {nextTimeClock && !isEndMatchingNextEntry && (
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                onClick={() =>
                  setClockOut(dayjs(nextTimeClock.info!.clockInDateTime))
                }>
                <SyncIcon />
              </IconButton>
              <Typography variant="body2" color="grey">
                Reset to match the next start time
              </Typography>
            </Stack>
          )}
          {isEndMatchingNextEntry && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckIcon color="success" />
              <Typography variant="body2" color="grey">
                Matched the next start time
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </form>
  )
}
